import type { TextAreaProps } from '@ksyos/design-system/dist/components/text-area';
import { FormGroup, TextArea } from '@ksyos/design-system';
import type { FormContextProps } from '@frontend/components/elements/formContext';
import { FormContextConsumer } from '@frontend/components/elements/formContext';
import { getValidationErrorTranslation } from '@frontend/utils/index';
import type { OmittedFormGroupFieldProps } from './FormGroup';
import { computeFormGroupProps } from './FormGroup';

type OmittedTextAreaProps = Omit<TextAreaProps, 'onChange' | 'value'>;

export interface TextAreaFieldProps extends OmittedTextAreaProps {
    onChange: (value: string | null) => void | Promise<void>;
    onDoValidate?: () => Promise<void>;
    value: string | null;
}

export function computeTextAreaProps(properties: TextAreaFieldProps): TextAreaProps {
    return {
        autoResize: properties.autoResize,
        disabled: properties.disabled,
        error: getValidationErrorTranslation(properties.error),
        maxLength: properties.maxLength,
        onBlur: properties.onBlur,
        onChange: async (event) => {
            const value = event.target.value === '' ? null : event.target.value;
            if (properties.onChange) {
                await properties.onChange(value);
            }
            if (properties.onDoValidate) {
                await properties.onDoValidate();
            }
        },
        placeholder: properties.placeholder,
        rows: properties.rows,
        size: properties.size,
        value: properties.value ? properties.value : '',
        variant: properties.variant,
    };
}

export type TextAreaFormGroupFieldProps = OmittedFormGroupFieldProps & TextAreaFieldProps;

export function TextAreaFormGroupField(props: FormContextProps<TextAreaFormGroupFieldProps>) {
    return (
        <FormContextConsumer {...props}>
            {(properties: any) => (
                <FormGroup {...computeFormGroupProps(properties)}>
                    <TextArea {...computeTextAreaProps(properties)} />
                </FormGroup>
            )}
        </FormContextConsumer>
    );
}
